import { Document } from '../types'

const STORAGE_KEY = 'dms_open_documents'
const MAX_OPEN_DOCUMENTS = 5

type StoredBucket = { documents: Document[]; dirtyIds: number[] }
type StoredV2 = { v: 2; byOrg: Record<string, StoredBucket> }

function emptyBucket(): StoredBucket {
  return { documents: [], dirtyIds: [] }
}

class OpenDocumentsService {
  private storage: StoredV2 = { v: 2, byOrg: {} }
  /** v1: plain array before per-org format */
  private legacyPlainList: Document[] | null = null
  private activeOrganizationId: number | null = null
  private openDocuments: Document[] = []
  private dirtyDocuments: Set<number> = new Set()
  private listeners: Set<() => void> = new Set()

  constructor() {
    this.loadFromSessionStorage()
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback())
  }

  private loadFromSessionStorage(): void {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        this.legacyPlainList = parsed
        return
      }
      if (parsed && parsed.v === 2 && parsed.byOrg && typeof parsed.byOrg === 'object') {
        this.storage = parsed as StoredV2
      }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  }

  private persistToSessionStorage(): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.storage))
    } catch {
      // ignore quota
    }
  }

  private memoryToBucket(): StoredBucket {
    return {
      documents: [...this.openDocuments],
      dirtyIds: [...this.dirtyDocuments],
    }
  }

  private applyBucketToMemory(bucket: StoredBucket): void {
    this.openDocuments = [...bucket.documents]
    this.dirtyDocuments = new Set(bucket.dirtyIds)
  }

  setActiveOrganizationId(organizationId: number | null): void {
    if (organizationId !== null && this.activeOrganizationId === organizationId) {
      return
    }

    if (this.activeOrganizationId !== null) {
      this.storage.byOrg[String(this.activeOrganizationId)] = this.memoryToBucket()
    }

    this.activeOrganizationId = organizationId

    if (organizationId === null) {
      this.openDocuments = []
      this.dirtyDocuments.clear()
      this.persistToSessionStorage()
      this.notifyListeners()
      return
    }

    if (this.legacyPlainList) {
      this.storage.byOrg[String(organizationId)] = {
        documents: this.legacyPlainList,
        dirtyIds: [],
      }
      this.legacyPlainList = null
    }

    const key = String(organizationId)
    const bucket = this.storage.byOrg[key] ?? emptyBucket()
    this.applyBucketToMemory(bucket)
    this.persistToSessionStorage()
    this.notifyListeners()
  }

  resetAllOrganizationsForLogout(): void {
    this.storage = { v: 2, byOrg: {} }
    this.legacyPlainList = null
    this.activeOrganizationId = null
    this.openDocuments = []
    this.dirtyDocuments.clear()
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore
    }
    this.notifyListeners()
  }

  save(): void {
    if (this.activeOrganizationId === null) {
      this.notifyListeners()
      return
    }
    this.storage.byOrg[String(this.activeOrganizationId)] = this.memoryToBucket()
    this.persistToSessionStorage()
    this.notifyListeners()
  }

  getOpenDocuments(): Document[] {
    return this.openDocuments
  }

  getOpenDocument(id: number): Document | undefined {
    return this.openDocuments.find((d) => d.id === id)
  }

  openDocument(doc: Document): boolean {
    if (this.openDocuments.find((d) => d.id === doc.id)) {
      return true
    }

    if (this.openDocuments.length >= MAX_OPEN_DOCUMENTS) {
      const docToRemove = this.openDocuments[MAX_OPEN_DOCUMENTS - 1]
      this.closeDocument(docToRemove)
    }

    this.openDocuments.unshift(doc)
    this.dirtyDocuments.delete(doc.id)
    this.save()
    return true
  }

  updateDocument(doc: Document): void {
    const index = this.openDocuments.findIndex((d) => d.id === doc.id)
    if (index > -1) {
      this.openDocuments[index] = doc
      this.dirtyDocuments.delete(doc.id)
      this.save()
    } else {
      this.openDocument(doc)
    }
  }

  updateDocumentFields(docId: number, changes: Partial<Document>): void {
    const index = this.openDocuments.findIndex((d) => d.id === docId)
    if (index > -1) {
      this.openDocuments[index] = {
        ...this.openDocuments[index],
        ...changes,
      }
      this.save()
    }
  }

  setDirty(doc: Document, dirty: boolean): void {
    const existingDoc = this.getOpenDocument(doc.id)
    if (!existingDoc) return

    if (dirty) {
      this.dirtyDocuments.add(doc.id)
    } else {
      this.dirtyDocuments.delete(doc.id)
    }

    this.save()
  }

  hasDirty(): boolean {
    return this.dirtyDocuments.size > 0
  }

  isDirty(doc: Document): boolean {
    return this.dirtyDocuments.has(doc.id)
  }

  closeDocument(doc: Document): boolean {
    const index = this.openDocuments.findIndex((d) => d.id === doc.id)
    if (index === -1) return true

    this.openDocuments.splice(index, 1)
    this.dirtyDocuments.delete(doc.id)
    this.save()
    return true
  }

  /** Closes all open documents for the current organization only. */
  closeAll(): boolean {
    this.openDocuments = []
    this.dirtyDocuments.clear()
    if (this.activeOrganizationId !== null) {
      this.storage.byOrg[String(this.activeOrganizationId)] = emptyBucket()
    }
    this.persistToSessionStorage()
    this.notifyListeners()
    return true
  }
}

export const openDocumentsService = new OpenDocumentsService()
